// App.js
import axios from "axios";
import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';

import Header from './components/Header';
import SeasonGamePage from './components/games/SeasonGamePage';
import ProfilePage from './components/profile/ProfilePage';
import ProfileEditSeason from './components/season/ProfileEditSeason';
import SeasonDetailPage from './components/season/SeasonDetailPage';
import ProfileSeasonDetailPage from './components/season/ProfileSeasonDetailPage';
import SeasonSelectPage from './components/season/SeasonSelectPage';
import SeasonTeams from './components/season/SeasonTeams';
import TeamDetailPage from './components/teams/TeamDetailPage';
import UserTeams from './components/user/UserTeams';
import ProtectedRoute from './components/utils/ProtectedRoute';
import GameDetailPage from './components/games/GameDetailPage';
import GameListPage from './components/games/GameListPage';
import Settings from './components/settings/Settings';
import SettingsLayout from './components/settings/SettingsLayout';
import NotFoundPage from './NotFoundPage';
import ProfileLayout from './components/profile/ProfileLayout';
import LeagueManagerLayout from './components/league/LeagueManagerLayout';
import LeagueManager from './components/league/LeagueManager';
import LeagueDetailPage from './components/league/LeagueDetailPage';
import ManagerSeasonList from './components/league/ManagerSeasonList';
import ManagerTeamListPage from './components/teams/ManagerTeamListPage';
import TeamManagerDetailPage from './components/teams/TeamManagerDetailPage';
import ManagerPlayerListPage from './components/league/ManagerPlayerListPage';
import ManagePlayerDetailPage from './components/league/ManagePlayerDetailPage';
import OrdersPage from './components/orders/ManagerOrderListPage';
import UserOrderHistoryPage from './components/orders/UserOrderHistoryPage';

import './App.css';
import './index.css';
import ThemeHandler from './components/ThemeHandler';
import PendingOrderProvider from './components/orders/PendingOrderProvider';
import ScrollToTop from './components/utils/ScrollToTop';

axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.xsrfHeaderName = "X-CSRFToken";

// Layout component that includes header
function MainLayout() {
  return (
    <>
      <Header />
      <main>
        <Outlet />
      </main>
    </>
  );
}

// Optional: You can keep your EnsureTrailingSlash component as is
const EnsureTrailingSlash = ({ children }) => {
  const location = useLocation();
  if (!location.pathname.endsWith('/')) {
    return (
      <Navigate
        to={{
          ...location,
          pathname: location.pathname + '/',
        }}
        replace
      />
    );
  }
  return children;
};

function App() {
  return (
    <Router>
      <ScrollToTop />
      <EnsureTrailingSlash>
        <PendingOrderProvider>
          <ThemeHandler />
          <Routes>
            {/*
              Routes that do not use the header: Login, Signup, NotFound.
              These are defined outside of the MainLayout.
            */}
            {/* <Route path="/login" element={<LoginPage />} /> */}
            {/* <Route path="/signup" element={<SignupPage />} /> */}
            <Route path="/not-found" element={<NotFoundPage />} />
            {/*
              Catch-all redirect for 404 pages
            */}
            <Route path="*" element={<Navigate to="/not-found" replace />} />

            {/*
              All routes that need the header are nested under MainLayout.
              They can use ProtectedRoute and your ProfileLayout as before.
            */}
            <Route element={<MainLayout />}>
              <Route
                path="/profile/"
                element={
                  <ProtectedRoute>
                    <ProfileLayout>
                      <ProfilePage />
                    </ProfileLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leagues/seasons/my-season/:seasonId/"
                element={
                  <ProtectedRoute>
                    <ProfileLayout>
                      <ProfileSeasonDetailPage />
                    </ProfileLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leagues/seasons/:seasonId/update/"
                element={
                  <ProtectedRoute>
                    <ProfileLayout>
                      <ProfileEditSeason />
                    </ProfileLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leagues/seasons/:seasonId/games/"
                element={
                  <ProtectedRoute>
                    <ProfileLayout>
                      <SeasonGamePage />
                    </ProfileLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leagues/seasons/select/"
                element={
                  <ProtectedRoute>
                    <ProfileLayout>
                      <SeasonSelectPage />
                    </ProfileLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leagues/seasons/:seasonId/teams/"
                element={
                  <ProtectedRoute>
                    <ProfileLayout>
                      <SeasonTeams />
                    </ProfileLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teams/user-teams/"
                element={
                  <ProtectedRoute>
                    <ProfileLayout>
                      <UserTeams />
                    </ProfileLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teams/:teamId/"
                element={
                  <ProtectedRoute>
                    <ProfileLayout>
                      <TeamDetailPage />
                    </ProfileLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/games/:gameId/"
                element={
                  <ProtectedRoute>
                    <ProfileLayout>
                      <GameDetailPage />
                    </ProfileLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/games/current-season/"
                element={
                  <ProtectedRoute>
                    <ProfileLayout>
                      <GameListPage />
                    </ProfileLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/orders/my-orders/"
                element={
                  <ProtectedRoute>
                    <ProfileLayout>
                      <UserOrderHistoryPage />
                    </ProfileLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/"
                element={
                  <ProtectedRoute>
                    <SettingsLayout>
                      <Settings />
                    </SettingsLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/league-manager/"
                element={
                  <ProtectedRoute>
                    <LeagueManagerLayout>
                      <LeagueManager />
                    </LeagueManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/league-manager/:leagueId/"
                element={
                  <ProtectedRoute>
                    <LeagueManagerLayout>
                      <LeagueDetailPage />
                    </LeagueManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/league-manager/seasons/:seasonId/details/"
                element={
                  <ProtectedRoute>
                    <LeagueManagerLayout>
                      <SeasonDetailPage />
                    </LeagueManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/league-manager/seasons/active"
                element={
                  <ProtectedRoute>
                    <LeagueManagerLayout>
                      <ManagerSeasonList />
                    </LeagueManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/league-manager/teams/"
                element={
                  <ProtectedRoute>
                    <LeagueManagerLayout>
                      <ManagerTeamListPage />
                    </LeagueManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/league-manager/teams/:teamId/"
                element={
                  <ProtectedRoute>
                    <LeagueManagerLayout>
                      <TeamManagerDetailPage />
                    </LeagueManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/league-manager/players"
                element={
                  <ProtectedRoute>
                    <LeagueManagerLayout>
                      <ManagerPlayerListPage />
                    </LeagueManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/league-manager/orders"
                element={
                  <ProtectedRoute>
                    <LeagueManagerLayout>
                      <OrdersPage />
                    </LeagueManagerLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/league-manager/players/:playerId/"
                element={
                  <ProtectedRoute>
                    <LeagueManagerLayout>
                      <ManagePlayerDetailPage />
                    </LeagueManagerLayout>
                  </ProtectedRoute>
                }
              />
            </Route>
          </Routes>
        </PendingOrderProvider>
      </EnsureTrailingSlash>
    </Router>
  );
}

export default App;
